import React from "react";
import { useEffect, useState } from "react";
//#region Types
import { TodoCardProps } from "models/propTypes/todoPropTypes";
import { WintSuperkollAPIModelAnnualAccounts } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
//#endregion
//#region Components
import AnnualReportAuditorTimeline from "components/annualAccount/annualReportAuditorSigningSteps/annualReportAuditorTimeline";
import AnnualReportAuditorSigningStepsFooter from "components/annualAccount/annualReportAuditorSigningSteps/footer";
import { EntityCardContent, SimpleTooltip, StaticInfoMessage, TitledContainerInner } from "components/common";
import { Description } from "components/common/wintForms";
import TopTodoCard from "components/todos/_partials/topTodoCard";
import { Grid } from "@mui/material";
import { useAnnualAccountService } from "serviceHooks";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import AuditorReportUploadToggle from "components/annualAccount/annualReportAuditorSigningSteps/auditorReportUploadToggle";
import FinancialStatementsApprovalSigneeSelect from "components/annualAccount/annualReportAuditorSigningSteps/financialStatementsApprovalSigneeSelect";

const AnnualReportAuditorSigningStepsTodo: React.FC<TodoCardProps> = (props) => {
  const { item } = props;
  const annualAccountService = useAnnualAccountService();
  const annualReportAuditorSigningSteps = item.auditorAnnualReportSigningSteps;
  const annualReportState =
    annualReportAuditorSigningSteps?.state ?? WintSuperkollAPIModelAnnualAccounts.SuperkollAnnualReportState.NotStarted; //The current state of the annual report
  const annualReportAuditorState =
    annualReportAuditorSigningSteps?.auditorState ??
    WintSuperkollAPIModelAnnualAccounts.SuperkollAuditorAnnualReportState.AuditorNotStarted; //The current auditor state of the annual report

  const getAnnualReportWithAuditorStateTranslations = (
    state: WintSuperkollAPIModelAnnualAccounts.SuperkollAnnualReportState,
    auditorState: WintSuperkollAPIModelAnnualAccounts.SuperkollAuditorAnnualReportState
  ) => {
    const steps = [
      {
        state: WintSuperkollAPIModelAnnualAccounts.SuperkollAnnualReportState.AnnualReportSigningStarted,
        auditorState: WintSuperkollAPIModelAnnualAccounts.SuperkollAuditorAnnualReportState.WaitingOnCustomer,
        title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Todos.TodoCardName_AnnualReportDocument),
        subHeaderText: LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.AnnualAccount.AnnualReportAuditorSigningsSubHeaderFirstState
        ),
      },
      {
        state: WintSuperkollAPIModelAnnualAccounts.SuperkollAnnualReportState.AnnualReportSigningStarted,
        auditorState: WintSuperkollAPIModelAnnualAccounts.SuperkollAuditorAnnualReportState.SigningStartedForAuditor,
        title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Todos.TodoCardName_AnnualReportDocument),
        subHeaderText: LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.AnnualAccount.AnnualReportAuditorSigningsSubHeaderSecondState
        ),
      },
      {
        state: WintSuperkollAPIModelAnnualAccounts.SuperkollAnnualReportState.AnnualReportSigningDone,
        auditorState: WintSuperkollAPIModelAnnualAccounts.SuperkollAuditorAnnualReportState.WaitingForAuditReport,
        title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Todos.TodoCardName_AuditorReportToUpload),
        subHeaderText: LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.AnnualAccount.AnnualReportAuditorSigningsSubHeaderThirdState
        ),
      },
      {
        state: WintSuperkollAPIModelAnnualAccounts.SuperkollAnnualReportState.AnnualReportSigningDone,
        auditorState: WintSuperkollAPIModelAnnualAccounts.SuperkollAuditorAnnualReportState.WaitingOnAuditorSign,
        title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Todos.TodoCardName_AuditorReportToSign),
        subHeaderText: LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.AnnualAccount.AnnualReportAuditorSigningsSubHeaderThirdState
        ),
      },
      {
        state: WintSuperkollAPIModelAnnualAccounts.SuperkollAnnualReportState.GeneralMeetingSigningStarted,
        auditorState: WintSuperkollAPIModelAnnualAccounts.SuperkollAuditorAnnualReportState.AuditorReportSigned,
        title: LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.Todos.TodoCardName_GeneralMeetingDocument
        ),
        subHeaderText: LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.AnnualAccount.AnnualReportAuditorSigningsSubHeaderFourthState
        ),
      },
      {
        state: WintSuperkollAPIModelAnnualAccounts.SuperkollAnnualReportState.XbrlFileSubmitted,
        auditorState: WintSuperkollAPIModelAnnualAccounts.SuperkollAuditorAnnualReportState.AuditorReportSigned,
        title: LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.Todos.TodoCardName_BolagsverketSubmission
        ),
        subHeaderText: LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.AnnualAccount.AnnualReportAuditorSigningsSubHeaderFifthState
        ),
      },
    ];

    const stepDetails = steps.find((step) => step.state === state && step.auditorState === auditorState);

    //fallback case
    return (
      stepDetails ?? {
        state: WintSuperkollAPIModelAnnualAccounts.SuperkollAnnualReportState.NotStarted,
        auditorState: WintSuperkollAPIModelAnnualAccounts.SuperkollAuditorAnnualReportState.AuditorNotStarted,
        title: "",
        subHeaderText: "",
      }
    );
  };

  const { title, subHeaderText } = getAnnualReportWithAuditorStateTranslations(
    annualReportState,
    annualReportAuditorState
  );

  let headerText = TranslationKeys.AnnualAccount.AnnualReportSigningStepsAtOneFlowHeader;

  if (annualReportState === WintSuperkollAPIModelAnnualAccounts.SuperkollAnnualReportState.XbrlFileSubmitted) {
    headerText = TranslationKeys.AnnualAccount.AnnualReportSigningStepsHeaderXbrlSubmitted;
  } else if (
    annualReportAuditorState ===
      WintSuperkollAPIModelAnnualAccounts.SuperkollAuditorAnnualReportState.WaitingForAuditReport ||
    annualReportAuditorState ===
      WintSuperkollAPIModelAnnualAccounts.SuperkollAuditorAnnualReportState.WaitingOnAuditorSign
  ) {
    headerText = TranslationKeys.AnnualAccount.AnnualReportSignAtAnyProgram;
  }

  const [auditReportUpload, setAuditReportUpload] = useState<{ IsConfirmSigningButtonEnabled: boolean }>({
    IsConfirmSigningButtonEnabled: false,
  });

  const setAuditReportUploadValue = async (IsConfirmSigningButtonEnabled: boolean) => {
    if (annualReportAuditorSigningSteps?.id !== undefined) {
      setAuditReportUpload({ IsConfirmSigningButtonEnabled });
      await annualAccountService.confirmAuditorReportUploaded(annualReportAuditorSigningSteps.financialYearId);
    }
  };

  const isAuditReportStep =
    annualReportAuditorState ===
      WintSuperkollAPIModelAnnualAccounts.SuperkollAuditorAnnualReportState.WaitingForAuditReport ||
    annualReportAuditorState ===
      WintSuperkollAPIModelAnnualAccounts.SuperkollAuditorAnnualReportState.WaitingOnAuditorSign;

  const [signees, setSignees] =
    useState<WintSuperkollAPIModelAnnualAccounts.FinancialStatementsApprovalSigneeOption[]>();
  const [signeesLoadFailed, setSigneesLoadFailed] = useState(false);
  const [signeePersonId, setSigneePersonId] = useState<number>();

  const financialYearId = annualReportAuditorSigningSteps?.financialYearId;

  const loadSignees = () => {
    setSigneesLoadFailed(financialYearId === undefined);
    setSignees(undefined);
    setSigneePersonId(undefined);
    if (financialYearId === undefined) return;

    annualAccountService
      .getFinancialStatementsApprovalSignees(financialYearId)
      .then((options) => {
        setSignees(options);
        if (options.length === 1) setSigneePersonId(options[0].personId);
      })
      .catch(() => setSigneesLoadFailed(true));
  };

  useEffect(() => {
    if (isAuditReportStep) loadSignees();
  }, [isAuditReportStep, financialYearId]);

  // Core accepts a confirm without a signer, so an empty list must not block the auditor.
  const isSigneeMissing = signeePersonId === undefined && signees?.length !== 0;

  const tooltipText = !auditReportUpload.IsConfirmSigningButtonEnabled
    ? TranslationKeys.AnnualAccount.AuditorReportUploadTooltip
    : isSigneeMissing
      ? TranslationKeys.AnnualAccount.FinancialStatementsApprovalSigneeTooltip
      : undefined;

  const shouldShowTooltip = isAuditReportStep && tooltipText !== undefined;

  return (
    <EntityCardContent
      topTodoCard={
        <TopTodoCard
          item={{
            ...props.item,
          }}
          overrideTitle={title}
          footerText={`${LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Common.Due_Date
          )}: ${LocalizationHelperInstance.getFormattedDate(DateHelper.addDays(props.item.date, 5))}`}
          descriptionHeadingTwo={annualReportAuditorSigningSteps?.companyName}
        />
      }
      todoMainSection={
        <TitledContainerInner
          header={LocalizationHelperInstance.getFormattedMessage(headerText)}
          subHeader={LocalizationHelperInstance.getFormattedMessage(subHeaderText)}
        >
          <AnnualReportAuditorTimeline annualReportState={annualReportState} auditorState={annualReportAuditorState} />
          {annualReportState ===
            WintSuperkollAPIModelAnnualAccounts.SuperkollAnnualReportState.AnnualReportSigningStarted &&
            annualReportAuditorState ===
              WintSuperkollAPIModelAnnualAccounts.SuperkollAuditorAnnualReportState.SigningStartedForAuditor && (
              <StaticInfoMessage>
                {LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.AnnualAccount.AnnualReportAuditorInfoMessage
                )}
              </StaticInfoMessage>
            )}
          {isAuditReportStep && (
            <Grid container direction="column" rowGap={3}>
              <AuditorReportUploadToggle
                value={auditReportUpload?.IsConfirmSigningButtonEnabled}
                setValue={setAuditReportUploadValue}
              />
              <FinancialStatementsApprovalSigneeSelect
                signees={signees}
                loadFailed={signeesLoadFailed}
                onRetry={loadSignees}
                value={signeePersonId}
                setValue={setSigneePersonId}
              />
            </Grid>
          )}
          {annualReportState === WintSuperkollAPIModelAnnualAccounts.SuperkollAnnualReportState.XbrlFileSubmitted && (
            <Description horizontal>
              {LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.AnnualAccount.AnnualReportSigningStepsBolagsverketDescription
              )}
            </Description>
          )}
        </TitledContainerInner>
      }
      footer={
        shouldShowTooltip ? (
          <SimpleTooltip placement="top" text={LocalizationHelperInstance.getFormattedMessage(tooltipText!)}>
            <Grid container>
              <AnnualReportAuditorSigningStepsFooter
                annualAccountReport={annualReportAuditorSigningSteps!}
                markAsDoneHandler={props.onActionSuccess}
                disabled={true}
                financialStatementsApprovalSigneePersonId={signeePersonId}
              />
            </Grid>
          </SimpleTooltip>
        ) : (
          <AnnualReportAuditorSigningStepsFooter
            annualAccountReport={annualReportAuditorSigningSteps!}
            markAsDoneHandler={props.onActionSuccess}
            disabled={false}
            financialStatementsApprovalSigneePersonId={signeePersonId}
          />
        )
      }
    ></EntityCardContent>
  );
};

export default AnnualReportAuditorSigningStepsTodo;
